import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "../lib/cn";

export type BadgeTone =
  | "neutral"
  | "volt"
  | "ember"
  | "success"
  | "warning"
  | "danger"
  | "info";

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: BadgeTone;
  /** Solid filled vs. soft tinted. */
  solid?: boolean;
  dot?: boolean;
  iconLeft?: ReactNode;
}

const soft: Record<BadgeTone, string> = {
  neutral: "bg-surface-sunken text-body-muted",
  volt: "bg-coral-soft text-volt-600",
  ember: "bg-violet-soft text-ember-600",
  success: "bg-lime-soft text-lime-600",
  warning: "bg-amber-soft text-amber-600",
  danger: "bg-coral-soft text-volt-600",
  info: "bg-ocean-soft text-ocean-600",
};

const solidMap: Record<BadgeTone, string> = {
  neutral: "bg-ink text-ondark",
  volt: "bg-grad-coral text-white shadow-volt",
  ember: "bg-grad-violet text-white shadow-ember",
  success: "bg-grad-lime text-white",
  warning: "bg-grad-amber text-white",
  danger: "bg-danger text-white",
  info: "bg-grad-ocean text-white",
};

const dotColor: Record<BadgeTone, string> = {
  neutral: "bg-body-faint",
  volt: "bg-coral",
  ember: "bg-violet",
  success: "bg-lime",
  warning: "bg-amber",
  danger: "bg-danger",
  info: "bg-ocean",
};

/** Compact status / category label. Soft by default; solid for emphasis. */
export function Badge({
  tone = "neutral",
  solid = false,
  dot = false,
  iconLeft,
  className,
  children,
  ...rest
}: BadgeProps) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-pill px-2.5 py-1 text-xs font-bold tracking-tight",
        solid ? solidMap[tone] : soft[tone],
        className,
      )}
      {...rest}
    >
      {dot && (
        <span className={cn("h-1.5 w-1.5 rounded-full", solid ? "bg-current opacity-80" : dotColor[tone])} />
      )}
      {iconLeft}
      {children}
    </span>
  );
}
